/**
 * Route copy for `src/routes/community` (keys: `routes.community.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  automationDecisionsViewer: {
    yes: 'yes',
    no: 'no',
    suggested: 'Suggested',
    draft: 'Draft',
    executed: 'Executed',
    blocked: 'Blocked',
    cancelled: 'Cancelled',
    couldNotLoadTheDecisionLog: 'Could not load the decision log.',
    decisionUndone: 'Decision undone.',
    cancellingFailed: 'Cancelling failed.',
    theCsvExportFailed: 'The CSV export failed.',
    automatedDecisionLog: 'Automated decision log',
    filterByOutcome: 'Filter by outcome',
    allOutcomes: 'All outcomes',
    exportTheLogLaw25: 'Export the log (Law 25)',
    csvExport: '⬇ CSV export',
    theDecisionLogIsNotAvailable: '⚠ The decision log is not available on this environment yet.',
    loading: 'Loading…',
    noAutomatedDecisionLogged: 'No automated decision logged.',
    confidenceV: '· confidence {{v}}%',
    contested: '· contested',
    contest: 'Contest',
    cancel: 'Cancel',
    contestRecorded: 'Contest recorded.',
    enterTheReasonForTheContest: 'Enter the reason for the contest.',
    theContestFailed: 'The contest failed.',
    contestAnAutomatedDecision: 'Contest an automated decision',
    sendTheContest: 'Send the contest',
    decision: 'Decision:',
    reasonForTheContest: 'Reason for the contest…',
    reasonForTheContest2: 'Reason for the contest',
    csvExportDownloadedRowsOne: 'CSV export downloaded ({{rows}} decision) — {{filename}}',
    csvExportDownloadedRowsPlural: 'CSV export downloaded ({{rows}} decisions) — {{filename}}',
    csvExportDownloaded: 'CSV export downloaded — {{filename}}',
  },
  automationPolicyPage: {
    ackBy: ' by {{who}}',
    couldNotLoadTheAutomationPolicy: 'Could not load the automation policy.',
    communityManagerAutomation: 'Community Manager automation',
    byDefaultTheAiOnlyDrafts: 'By default, the AI only drafts and suggests — nothing is sent, reacted to or profiled automatically. Enabling an automation requires accepting the Law 25 notice (automated decision).',
    theAutomationPolicyIsNotAvailable: '⚠ The automation policy is not available on this environment yet. The values shown are the defaults (everything off).',
    loadingTheAutomationPolicy: 'Loading the automation policy',
    loading: 'Loading…',
    automationPolicySaved: 'Automation policy saved.',
    allAutomationsHaveBeenDisabled: 'All automations have been disabled.',
    savingFailed: 'Saving failed.',
    theAutomaticSendConfidenceThresholdMust: 'The automatic-send confidence threshold must be ≥ 0.80.',
    aiDraftingOnEveryComment: 'AI drafting on every comment',
    theAiDraftsAReplyFor: 'The AI drafts a reply for every incoming comment (kept for human approval — nothing is sent).',
    automaticReplySending: 'Automatic reply sending',
    sendsAnAiValidatedReplyWithout: 'Sends an AI-validated reply without human intervention, only if confidence exceeds the threshold, outside quiet hours, under the daily cap, and after the CASL/Law 25 compliance filter.',
    minimumConfidenceThreshold080: 'Minimum confidence threshold (≥ 0.80)',
    platformsAllowedForAutomaticSending: 'Platforms allowed for automatic sending',
    stillRequireAReviewerInThe: 'Still require a reviewer in the loop',
    automaticReactionLike: 'Automatic reaction (like)',
    reactsAutomaticallyToIncomingInteraction: 'Reacts automatically to incoming interactions, where the platform API allows it (otherwise honestly skipped).',
    commentAnalysisThemesProfiling: 'Comment analysis → themes (profiling)',
    groupsDeIdentifiedCommentsToSurface: 'Groups (de-identified) comments to surface themes and questions. Law 25 profiling consent — off by default.',
    dailyActionCap: 'Daily action cap',
    quietHoursStart: 'Quiet hours — start',
    quietHoursEnd: 'Quiet hours — end',
    law25NoticeAcceptedOn: '✓ Law 25 notice accepted on',
    version: '(version',
    saveThePolicy: 'Save the policy',
    automationsAreEnabledActionsWillBe: '⚠ Automations are enabled — actions will be audited.',
    active: 'ACTIVE',
    automatedDecisionNoticeLaw25: 'Automated decision notice (Law 25)',
    cancel: 'Cancel',
    acceptAndEnable: 'Accept and enable',
    byEnablingAnAutomationAutomaticSending: 'By enabling an automation (automatic sending, automatic reaction or comment profiling), you authorize the AI Community Manager to make automated decisions on your behalf, within the limits you set.',
    everyAutomatedActionIsLoggedAnd: 'Every automated action is logged and auditable (decision log).',
    everyActionStaysSubjectToThe: 'Every action stays subject to the CASL / Law 25 compliance filter (closed by default); a failure downgrades the action to a draft.',
    youCanContestOrUndoA: 'You can contest or undo a decision, and turn off every automation in a single click (kill switch).',
    commentProfilingIsDeIdentifiedNo: 'Comment profiling is de-identified; no author data is stored.',
    iUnderstandAndAcceptThatAutomated: 'I understand and accept that automated decisions are made under this policy.',
    theKillSwitchFailed: 'The kill switch failed.',
    killSwitch: 'Kill switch',
    immediatelyDisablesEveryAutomationAutoSe: 'Immediately disables every automation (auto send, auto reaction, profiling) in a single audited call.',
    disableEverything: '⛔ Disable everything',
    disableAllAutomations: 'Disable all automations?',
    everyCommunityManagerAutomationWillBe: 'Every Community Manager automation will be disabled immediately. This action is logged.',
    disableEverything2: 'Disable everything',
  },
  cannedMacrosPage: {
    replyDraft: 'Reply (draft)',
    insertASavedReply: 'Insert a saved reply',
    addTags: 'Add tags',
    assign: 'Assign',
    changeTheStatus: 'Change the status',
    title: 'Title',
    shortcutOptional: 'Shortcut (optional)',
    bodyNameVariablesRenderedOnInsertion: 'Body ({name} variables rendered on insertion)',
    tagsCommaSeparated: 'Tags (comma-separated)',
    couldNotLoadTheSavedReplies: 'Could not load the saved replies.',
    savedReplies: 'Saved replies',
    reusableRepliesWithNameVariablesInsertab: 'Reusable replies with {name} variables, insertable into a conversation.',
    newReply: '+ New reply',
    newSavedReply: 'New saved reply',
    createTheReply: 'Create the reply',
    savedReplyCreated: 'Saved reply created.',
    loading: 'Loading…',
    noSavedReplyYet: 'No saved reply yet.',
    active: 'Active',
    disabled: 'Disabled',
    view: 'View',
    disable: 'Disable',
    enable: 'Enable',
    edit: 'Edit',
    delete: 'Delete',
    editTheReply: 'Edit the reply',
    save: 'Save',
    replyUpdated: 'Reply updated.',
    deleteTheReply: 'Delete the reply',
    deleteTitleThisCannotBeUndone: 'Delete “{{title}}”? This cannot be undone.',
    replyDeleted: 'Reply deleted.',
    theTitleAndTheBodyAre: 'The title and the body are required.',
    savingFailed: 'Saving failed.',
    eGThankYou: 'E.g. Thank you',
    bodyVariables: 'Body (variables',
    name: '{name}',
    renderedOnInsertion: 'rendered on insertion)',
    helloNameThankYouForYour: 'Hello {name}, thank you for your message…',
    supportEn: 'support, en',
    replyActive: 'Reply active',
    cancel: 'Cancel',
    emptyTemplate: '(empty template)',
    none: '(none)',
    noTag: '(no tag)',
    unassigned: '(unassigned)',
    noStatus: '(no status)',
    couldNotLoadTheMacros: 'Could not load the macros.',
    macros: 'Macros',
    actionBundlesReplyTagAssignChange: 'Action bundles (reply, tag, assign, change status) applied in one click.',
    newMacro: '+ New macro',
    newMacro2: 'New macro',
    createTheMacro: 'Create the macro',
    macroCreated: 'Macro created.',
    noMacroConfigured: 'No macro configured.',
    stepS: 'step(s)',
    editTheMacro: 'Edit the macro',
    macroUpdated: 'Macro updated.',
    deleteTheMacro: 'Delete the macro',
    deleteNameThisCannotBeUndone: 'Delete “{{name}}”? This cannot be undone.',
    macroDeleted: 'Macro deleted.',
    theNameIsRequired: 'The name is required.',
    addAtLeastOneStep: 'Add at least one step.',
    name2: 'Name',
    eGResolveThank: 'E.g. Resolve + thank',
    descriptionOptional: 'Description (optional)',
    whatThisMacroIsFor: 'What this macro is for',
    steps: 'Steps',
    addAStep: '+ Add a step',
    noStepsAddAtLeastOne: 'No steps — add at least one.',
    typeOfStepV: 'Type of step {{v}}',
    deleteStepV: 'Delete step {{v}}',
    replyTemplateOfStepV: 'Reply template of step {{v}}',
    helloName: 'Hello {name}, …',
    savedReplyOfStepV: 'Saved reply of step {{v}}',
    chooseAReply: '— Choose a reply —',
    tagsOfStepV: 'Tags of step {{v}}',
    vipResolved: 'vip, resolved',
    assignmentOfStepV: 'Assignment of step {{v}}',
    userIdentifier: 'User identifier',
    statusOfStepV: 'Status of step {{v}}',
    chooseAStatus: '— Choose a status —',
    macroActive: 'Macro active',
    theUpdateFailed: 'The update failed.',
    deletionFailed: 'Deletion failed.',
  },
  cannedResponseDetailPage: {
    couldNotLoadTheSavedReply: 'Could not load the saved reply.',
    loading: 'Loading…',
    backToSavedReplies: '← Back to saved replies',
    replyNotFound: 'Reply not found.',
    active: 'Active',
    disabled: 'Disabled',
    idIdTenantTenantId: 'ID {{id}} · Tenant {{tenant_id}}',
    body: 'Body',
    tags: 'Tags',
    noTag: 'No tag',
    createdOn: 'Created on',
    updatedOn: 'Updated on',
  },
  commentsDashboard: {
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
    mixed: 'Mixed',
    couldNotLoadTheCommentsDashboard: 'Could not load the comments dashboard.',
    createdAiDraftSCreatedTo: '{{created}} AI draft(s) created — to approve in the inbox.',
    draftsStoppedInsufficientCredits: '{{created}} AI draft(s) created; {{stopped}} not created: insufficient AI credits — top up to continue.',
    noNewDraftToCreate: 'No new draft to create.',
    creatingTheDraftsFailed: 'Creating the drafts failed.',
    commentsPerPost: 'Comments per post',
    commentCoverageForEachPostTotal: 'Comment coverage for each post: total, unanswered, answered by AI / human, time to first reply, sentiment and honest per-platform capabilities. Bulk AI drafting only creates drafts.',
    theCommentsDashboardIsNotAvailable: '⚠ The comments dashboard is not available on this environment yet.',
    unansweredComments: 'Unanswered comments',
    conversationsToHandle: 'Conversations to handle',
    draftsToApprove: 'Drafts to approve',
    autoReplies: 'Auto replies',
    unansweredPerPlatform: 'Unanswered per platform:',
    aLegacyAutonomousModerationRuleIs: '⚠ A legacy “autonomous” moderation rule is still active — it must be revalidated under the new model (suggestion / draft / auto send).',
    filterByPlatform: 'Filter by platform',
    allPlatforms: 'All platforms',
    onlyWithUnansweredComments: 'Only with unanswered comments',
    refresh: 'Refresh',
    loading: 'Loading…',
    noPostWithCommentsForThese: 'No post with comments for these filters.',
    postV: 'Post {{v}}',
    writing: 'Writing…',
    draftUnansweredAiReplyIes: '✨ Draft {{unanswered}} AI reply(ies)',
    comments: 'Comments',
    unanswered: 'Unanswered',
    answeredByAi: 'Answered by AI',
    answeredByAHuman: 'Answered by a human',
    n1stReply: '1st reply',
    viewUnansweredComments: 'View unanswered comments →',
  },
  communityLayout: {
    community: 'Community',
    socialInboxProductivitySavedRepliesMacro: 'Social inbox productivity: saved replies, macros, SLA, customer satisfaction and influencer campaigns.',
  },
  competitiveVoicePage: {
    couldNotLoadTheShareOf: 'Could not load the share of voice.',
    shareOfVoiceCompetitors: 'Share of voice & competitors',
    trackBrandsYoursOrRivalsAnd: 'Track brands (yours or rivals) and compare their share of the mentions actually ingested, with the tone.',
    newBrand: '+ New brand',
    shareOfVoice: 'Share of voice',
    trackedMentionS: 'tracked mention(s)',
    yourShareV: ' · your share: {{v}}',
    noTrackedMentionInThePeriod: 'No tracked mention in the period. Add brands and let the inbox ingest interactions to feed this calculation.',
    you: 'you',
    toneNetSentiment: 'tone {{net_sentiment}}',
    loading: 'Loading…',
    noTrackedBrandAddYourBrand: 'No tracked brand. Add your brand and your competitors to get started.',
    yourBrand: 'your brand',
    disabled: 'disabled',
    queryQuery: 'query: {{query}}',
    mentionSVShare: 'mention(s) · {{v}} share',
    edit: 'Edit',
    delete: 'Delete',
    newTrackedBrand: 'New tracked brand',
    add: 'Add',
    brandAdded: 'Brand added.',
    editName: 'Edit “{{name}}”',
    save: 'Save',
    brandUpdated: 'Brand updated.',
    deleteTheBrand: 'Delete the brand',
    nameWillNoLongerBeTracked: '“{{name}}” will no longer be tracked in the share of voice.',
    brandDeleted: 'Brand deleted.',
    couldNotDelete: 'Could not delete.',
    savingFailedNameAlreadyTaken: 'Saving failed (name already taken?).',
    name: 'Name',
    eGCompetitorInc: 'E.g. Competitor Inc.',
    matchingQuery: 'Matching query',
    keywordsBrandToSpotInMentions: 'keywords / brand to spot in mentions',
    accountsHandles: 'Accounts (handles)',
    brandBrandEn: '@brand, @brand_en',
    platforms: 'Platforms',
    instagramFacebookX: 'instagram, facebook, x',
    thisIsMyBrand: 'This is my brand',
    enabled: 'Enabled',
    cancel: 'Cancel',
  },
  coverageBadges: {
    publishable: 'Publishable',
    reaction: 'reaction',
    commentOnly: 'Comment only',
    noActionAvailable: 'No action available',
    blocked: 'Blocked',
  },
  csatDashboardPage: {
    couldNotLoadTheCsatData: 'Could not load the CSAT data.',
    customerSatisfactionCsat: 'Customer satisfaction (CSAT)',
    responseRateAverageScoreAndDistribution: 'Response rate, average score and distribution of the surveys sent after resolution.',
    refresh: 'Refresh',
    loading: 'Loading…',
    surveysSent: 'Surveys sent',
    responses: 'Responses',
    replyRate: 'Reply rate',
    averageScore: 'Average score',
    noSurveySentYetSendA: 'No survey sent yet. Send a CSAT survey from a resolved conversation (inbox details panel).',
    scoreDistribution: 'Score distribution',
    surveySSentAwaitingAResponse: 'survey(s) sent, awaiting a response.',
    surveysLength: 'Surveys ({{length}})',
    noSurveyRecorded: 'No survey recorded.',
    pending: 'Pending',
    conversationVSentOn: 'Conversation {{v}}… · sent on',
    answeredOnV: ' · answered on {{v}}',
  },
  csatPublicApi: {
    csatApiErrorStatus: 'CSAT API error {{status}}',
  },
  editorialProfilesPage: {
    indexPrice: 'Indexing: 1 credit per file, 5 credits per URL.',
    wholeAccount: 'Whole account',
    project: 'Project',
    page: 'Page',
    editorialLineName: 'Editorial line name',
    scopeAccountProjectPage: 'Scope (account / project / page)',
    targetedProjectOrPage: 'Targeted project or page',
    toneAndStyle: 'Tone and style',
    brandVoice: 'Brand voice',
    allowedTopics: 'Allowed topics',
    forbiddenTopics: 'Forbidden topics',
    escalationRules: 'Escalation rules',
    haveAnAgentReviewBeforePublishing: 'Have an agent review before publishing',
    couldNotLoadTheEditorialLines: 'Could not load the editorial lines.',
    aiCommunityManagerEditorialLines: 'AI Community Manager editorial lines',
    definePerPageOrPerProject: 'Define, per page or per project, the tone, brand voice, allowed/forbidden topics, escalation rules and the knowledge base the AI CM draws on.',
    newEditorialLine: '+ New editorial line',
    scope: 'Scope',
    all: 'All',
    newEditorialLine2: 'New editorial line',
    createTheEditorialLine: 'Create the editorial line',
    editorialLineCreated: 'Editorial line created.',
    loading: 'Loading…',
    noEditorialLineConfiguredForThis: 'No editorial line configured for this scope.',
    agentReviewRequired: 'Agent review required',
    noToneDefined: 'No tone defined',
    voiceBrandvoicename: '· Voice: {{brandVoiceName}}',
    vDocS: '· 📚 {{v}} doc(s)',
    documents: 'Documents',
    edit: 'Edit',
    delete: 'Delete',
    editTheEditorialLine: 'Edit the editorial line',
    save: 'Save',
    editorialLineUpdated: 'Editorial line updated.',
    knowledgeBaseName: 'Knowledge base — {{name}}',
    deleteTheEditorialLine: 'Delete the editorial line',
    deleteNameThisCannotBeUndone: 'Delete “{{name}}”? This cannot be undone.',
    editorialLineDeleted: 'Editorial line deleted.',
    theNameIsRequired: 'The name is required.',
    selectTheTargetedProject: 'Select the targeted project.',
    selectTheTargetedPage: 'Select the targeted page.',
    savingFailed: 'Saving failed.',
    eGCaringSupportEnPage: 'E.g. Caring support — EN page',
    select: '— Select —',
    noneToneOnly: 'None (tone only)',
    noBrandVoiceSavedCreateOne: 'No brand voice saved — create one from the AI suite to link it here.',
    allowedTopicsOnePerLine: 'Allowed topics (one per line)',
    productsDeliveryOpeningHours: 'products\ndelivery\nopening hours',
    forbiddenTopicsOnePerLine: 'Forbidden topics (one per line)',
    politicsCompetitorsUnpublishedPrices: 'politics\ncompetitors\nunpublished prices',
    eGHandOffToA: 'E.g. Hand off to a human if the customer is unhappy, asks for a refund, or mentions a legal issue.',
    theAiCmSRepliesWill: 'The AI CM\'s replies will be held for human approval before being published.',
    cancel: 'Cancel',
    couldNotLoadTheDocuments: 'Could not load the documents.',
    onlyPdfFilesAreAccepted: 'Only PDF files are accepted.',
    pdfDocumentAddedToTheKnowledge: 'PDF document added to the knowledge base.',
    addingTheDocumentFailed: 'Adding the document failed.',
    enterAValidUrlHttpS: 'Enter a valid URL (http(s)://…).',
    urlAddedToTheKnowledgeBase: 'URL added to the knowledge base.',
    addingTheUrlFailed: 'Adding the URL failed.',
    theAiCmDrawsItsAnswers: 'The AI CM draws its answers from these sources. Add PDFs (product sheets, FAQ, policies) or publicly accessible URLs.',
    uploadAPdf: 'Upload a PDF',
    addAUrl: 'Add a URL',
    add: 'Add',
    noDocumentInTheKnowledgeBase: 'No document in the knowledge base.',
    pdfDocument: 'PDF document',
    addedOnV: 'Added on {{v}}',
    remove: 'Remove',
    removeTheDocument: 'Remove the document',
    removeTitleFromTheKnowledgeBase: 'Remove “{{title}}” from the knowledge base?',
    documentRemoved: 'Document removed.',
    deletionFailed: 'Deletion failed.',
  },
  engagerRulesPage: {
    couldNotLoadTheEngagementRules: 'Could not load the engagement rules.',
    engagementRules: 'Engagement rules',
    triggerActionsLabelGroupLifecycleSignal: 'Trigger actions (label, group, lifecycle, signal, enrichment) on incoming interactions based on platform, type, keywords, sentiment or intent.',
    newRule: '+ New rule',
    engagementRulesAreNotAvailableOn: '⚠ Engagement rules are not available on this environment yet.',
    newEngagementRule: 'New engagement rule',
    createRule: 'Create rule',
    engagementRuleCreated: 'Engagement rule created.',
    loading: 'Loading…',
    noEngagementRuleConfigured: 'No engagement rule configured.',
    active: 'Active',
    disabled: 'Disabled',
    priorityPriority: 'Priority {{priority}}',
    ifV: 'If: {{v}}',
    disable: 'Disable',
    enable: 'Enable',
    edit: 'Edit',
    delete: 'Delete',
    editTheRule: 'Edit the rule',
    save: 'Save',
    ruleUpdated: 'Rule updated.',
    deleteTheRule: 'Delete the rule',
    deleteNameThisCannotBeUndone: 'Delete “{{name}}”? This cannot be undone.',
    ruleDeleted: 'Rule deleted.',
    theNameIsRequired: 'The name is required.',
    addAtLeastOneCompleteAction: 'Add at least one complete action (with its value when required).',
    savingFailed: 'Saving failed.',
    name: 'Name',
    eGHighIntentCommentsLabel: 'E.g. High-intent comments → label + enrich',
    priorityHighestFirst: 'Priority (highest first)',
    conditionsAllMustMatchEmptyAny: 'Conditions (all must match; empty = any)',
    platforms: 'Platforms',
    interactionType: 'Interaction type',
    sentiment: 'Sentiment',
    keywordsCommaSeparated: 'Keywords (comma-separated)',
    priceDemoPricing: 'price, demo, pricing',
    highPurchaseIntentOnly: 'High purchase intent only',
    actions: 'Actions',
    action: 'Action',
    removeTheAction: 'Remove the action',
    addAnAction: '+ Add an action',
    ruleActive: 'Rule active',
    cancel: 'Cancel',
    label: 'Label',
    groupIdName: 'Group ID / name',
    lifecycleStage: 'Lifecycle stage',
    signalKey: 'Signal key',
    value: 'Value',
    theUpdateFailed: 'The update failed.',
    deletionFailed: 'Deletion failed.',
    stopsEvaluation: 'Stops evaluation',
    keywordMatchMode: 'Keyword matching',
    allKeywords: 'All keywords',
    anyKeyword: 'Any keyword',
    regexOptionalCaseInsensitive: 'Regular expression (optional, case-insensitive)',
    regexPlaceholder: '(demo|trial)',
    invalidRegex: 'Invalid regular expression.',
    adRepliesOnly: 'Ad replies only',
    stopEvaluatingNextRulesIfMatch: 'Stop evaluating the following rules if this one matches',
  },
  influencerCampaignDetailPage: {
    guest: 'Guest',
    accepted: 'Accepted',
    declined: 'Declined',
    contentSubmitted: 'Content submitted',
    contentApproved: 'Content approved',
    delivered: 'Delivered',
    name: 'Name',
    handle: 'Handle / @',
    platform: 'Platform',
    couldNotLoadTheCampaign: 'Could not load the campaign.',
    statusUpdated: 'Status updated.',
    theUpdateFailed: 'The update failed.',
    campaignDeleted: 'Campaign deleted.',
    deletionFailed: 'Deletion failed.',
    loading: 'Loading…',
    backToCampaigns: '← Back to campaigns',
    campaignNotFound: 'Campaign not found.',
    budget: 'Budget:',
    campaignStatus: 'Campaign status',
    delete: 'Delete',
    participantsLength: 'Participants ({{length}})',
    identifyInfluencers: '🔍 Identify influencers',
    addAParticipant: '+ Add a participant',
    noParticipantsAddInfluencersToThis: 'No participants. Add influencers to this campaign.',
    addAParticipant2: 'Add a participant',
    participantAdded: 'Participant added.',
    vEnrolled: '{{v}} enrolled.',
    deleteTheCampaign: 'Delete the campaign',
    deleteNameAndAllItsParticipants: 'Delete “{{name}}” and all its participants? Irreversible.',
    lengthDeliverables: '/{{length}} deliverables',
    reviewNoteReviewNotes: 'Review note: {{review_notes}}',
    toggleTheDeliverable: 'Toggle the deliverable',
    contentReview: 'Content review',
    saveTheSubmittedContent: 'Save the submitted content',
    contentApproved2: 'Content approved.',
    contentRejected: 'Content rejected.',
    contentSavedAwaitingReview: 'Content saved (awaiting review).',
    contentReviewHil: 'Content review (HIL)',
    reviewNotesOptional: 'Review notes (optional)',
    feedbackForTheInfluencer: 'Feedback for the influencer…',
    approve: 'Approve',
    reject: 'Reject',
    cancel: 'Cancel',
    theContentIsRequired: 'The content is required.',
    content: 'Content',
    proposedPostText: 'Proposed post text…',
    linksOnePerLineOptional: 'Links (one per line, optional)',
    save: 'Save',
    enterAtLeastANameOr: 'Enter at least a name or a handle.',
    addingFailed: 'Adding failed.',
    janeDoe: 'Jane Doe',
    handle2: 'Handle',
    jane: '@jane',
    instagram: 'instagram',
    contactEmailOptional: 'Contact email (optional)',
    deliverables: 'Deliverables',
    eG1FeedPost: 'E.g. 1 feed post',
    newDeliverable: 'New deliverable',
    add: 'Add',
    removeLabel: 'Remove {{label}}',
    addTheParticipant: 'Add the participant',
    identificationFailed: 'Identification failed.',
    identifyInfluencers2: 'Identify influencers',
    publicAuthorsCommentsMentionsReviewsNeve: 'Public authors (comments / mentions / reviews — never DMs) who engage with you the most, ranked by volume, tone and real reach. Enroll them into this campaign in one click.',
    all: 'all',
    minMentions: 'Min. mentions',
    refresh: 'Refresh',
    analyzing: 'Analyzing…',
    noAuthorIdentifiedLetTheInbox: 'No author identified. Let the inbox ingest public interactions to feed this ranking.',
    author: 'Author',
    scoreScore: 'score {{score}}',
    mentionsMentionS: '{{mentions}} mention(s)',
    reach: 'reach',
    enroll: 'Enroll',
    close: 'Close',
    influencer: 'Influencer',
    updatingTheDeliverableFailed: 'Updating the deliverable failed.',
    theApprovalFailed: 'The approval failed.',
    theRejectionFailed: 'The rejection failed.',
    savingFailed: 'Saving failed.',
  },
  influencerCampaignsPage: {
    draft: 'Draft',
    active: 'Active',
    completed: 'Completed',
    archived: 'Archived',
    name: 'Name',
    status: 'Status',
    budgetOptional: 'Budget ($, optional)',
    startOptional: 'Start (optional)',
    endOptional: 'End (optional)',
    couldNotLoadTheCampaigns: 'Could not load the campaigns.',
    influencerCampaigns: 'Influencer campaigns',
    briefsBudgetsParticipantsAndDeliverables: 'Briefs, budgets, participants and deliverables — from recruiting to content approval.',
    filterByStatus: 'Filter by status',
    allStatuses: 'All statuses',
    newCampaign: '+ New campaign',
    analysisRecommendationsCampaigns: 'Analysis & recommendations — campaigns',
    newCampaign2: 'New campaign',
    campaignCreated: 'Campaign created.',
    loading: 'Loading…',
    noCampaignsYet: 'No campaigns yet.',
    budget: 'Budget',
    noWindowDefined: 'No window defined',
    open: 'Open →',
    theNameIsRequired: 'The name is required.',
    savingFailed: 'Saving failed.',
    eGSummer2026Launch: 'E.g. Summer 2026 launch',
    briefGoalOptional: 'Brief goal (optional)',
    eGAwareness3Posts5: 'E.g. Awareness, 3 posts + 5 stories',
    createTheCampaign: 'Create the campaign',
    cancel: 'Cancel',
  },
  kit: {
    readOnlyYourRoleCannotCreate: '🔒 Read-only — your role cannot create or edit these items.',
  },
  macroDetailPage: {
    emptyTemplate: '(empty template)',
    replyCannedIdNotFound: 'Reply {{canned_id}} (not found)',
    noReplyChosen: '(no reply chosen)',
    noTag: '(no tag)',
    unassigned: '(unassigned)',
    noStatus: '(no status)',
    couldNotLoadTheMacro: 'Could not load the macro.',
    loading: 'Loading…',
    backToSavedReplies: '← Back to saved replies',
    macroNotFound: 'Macro not found.',
    active: 'Active',
    disabled: 'Disabled',
    idIdTenantTenantId: 'ID {{id}} · Tenant {{tenant_id}}',
    stepsLength: 'Steps ({{length}})',
    noSteps: 'No steps.',
    createdOn: 'Created on',
    updatedOn: 'Updated on',
  },
  monitorDetailPage: {
    abnormalSpikeDetection: 'abnormal spike detection',
    winMinWindowNoThresholdConfigured: '{{win}}-min window — no threshold configured',
    loading: 'Loading…',
    backToSocialListening: '← Back to social listening',
    monitoringIsNotAvailableOnThis: 'Monitoring is not available on this environment yet (service being deployed). Try again later.',
    couldNotLoadThisMonitor: 'Could not load this monitor.',
    tryAgain: 'Try again',
    active: 'Active',
    inactive: 'Inactive',
    idIdTenantTenantId: 'ID {{id}} · Tenant {{tenant_id}}',
    queryKeywords: 'Query (keywords)',
    platforms: 'Platforms',
    allPlatformsLengthTracked: 'All platforms ({{length}} tracked)',
    alertRule: 'Alert rule',
    createdOn: 'Created on',
    updatedOn: 'Updated on',
  },
  publicCsatPage: {
    veryDissatisfied: 'Very dissatisfied',
    dissatisfied: 'Dissatisfied',
    neutral: 'Neutral',
    satisfied: 'Satisfied',
    verySatisfied: 'Very satisfied',
    thisLinkIsTemporarilyUnavailableTry: 'This link is temporarily unavailable. Try again.',
    sendingFailedTryAgain: 'Sending failed. Try again.',
    loading: 'Loading…',
    surveyNotFound: 'Survey not found',
    thisLinkIsInvalidOrHas: 'This link is invalid or has expired.',
    thankYouForYourFeedback: 'Thank you for your feedback!',
    yourRatingDonescore5HasBeen: 'Your rating ({{doneScore}}/5) has been recorded.',
    yourOpinionMatters: 'Your opinion matters',
    pleaseTakeAFewSecondsTo: 'Please take a few seconds to rate the support you received.',
    commentOptional: 'Comment (optional)',
    tellUsMoreOptional: 'Tell us more (optional)…',
    sending: 'Sending…',
    sendMyAnswer: 'Send my answer',
    poweredByKarexia: 'Powered by Karexia',
  },
  slaPoliciesPage: {
    scanning: 'Scanning…',
    scanForBreaches: 'Scan for breaches',
    alert: 'Alert',
    vHV2Min: '{{v}} h {{v2}} min',
    name: 'Name',
    n1stReplyMinutes: '1st reply (minutes)',
    resolutionMinutes: 'Resolution (minutes)',
    actionOnBreach: 'Action on breach',
    priorityHigherTakesPrecedence: 'Priority (higher = takes precedence)',
    couldNotLoadTheSlaPolicies: 'Could not load the SLA policies.',
    checkedClockSCheckedLengthBreach: '{{checked}} clock(s) checked, {{length}} breach(es).',
    theBreachScanFailed: 'The breach scan failed.',
    slaPolicies: 'SLA policies',
    firstReplyAndResolutionTargetsWith: 'First-reply and resolution targets, with escalation or alert on breach.',
    newPolicy: '+ New policy',
    lastScanCheckedClockSChecked: 'Last scan: {{checked}} clock(s) checked',
    breachEs: 'breach(es)',
    conversationV: 'Conversation {{v}}…',
    startedOnV: 'Started on {{v}}',
    newSlaPolicy: 'New SLA policy',
    createThePolicy: 'Create the policy',
    slaPolicyCreated: 'SLA policy created.',
    loading: 'Loading…',
    noSlaPolicyConfigured: 'No SLA policy configured.',
    active: 'Active',
    disabled: 'Disabled',
    priorityPriority: 'Priority {{priority}}',
    n1stReplyVResolution: '1st reply: {{v}} · Resolution:',
    view: 'View',
    disable: 'Disable',
    enable: 'Enable',
    edit: 'Edit',
    delete: 'Delete',
    editThePolicy: 'Edit the policy',
    save: 'Save',
    policyUpdated: 'Policy updated.',
    deleteThePolicy: 'Delete the policy',
    deleteNameThisCannotBeUndone: 'Delete “{{name}}”? This cannot be undone.',
    policyDeleted: 'Policy deleted.',
    theNameIsRequired: 'The name is required.',
    enterAtLeastOneTarget1st: 'Enter at least one target (1st reply or resolution).',
    durationsMustBeIntegers1Minute: 'Durations must be integers ≥ 1 minute.',
    savingFailed: 'Saving failed.',
    eGVipFastReply: 'E.g. VIP — fast reply',
    policyActive: 'Policy active',
    cancel: 'Cancel',
    responseTimeReport: 'Response time report',
    n1stReplyAvg: '1st reply (avg.)',
    medianV: 'median {{v}}',
    resolutionAvg: 'Resolution (avg.)',
    onTime: 'On time',
    respondedAnsweredResolvedResolved: '{{responded}} answered / {{resolved}} resolved',
    breaches: 'Breaches',
    firstResponseBreaches1stReplResolution: '{{first_response_breaches}} 1st repl. · {{resolution_breaches}} resol.',
    aggregatedFromClocksSlaClockS: 'Aggregated from {{clocks}} SLA clock(s) over {{conversations}} conversation(s). Zero values until a reply/resolution has happened — never a made-up figure.',
    escalate: 'Escalate',
    theUpdateFailed: 'The update failed.',
    deletionFailed: 'Deletion failed.',
  },
  slaPolicyDetailPage: {
    alert: 'Alert',
    notSet: '— (not set)',
    vHV2Min: '{{v}} h {{v2}} min',
    couldNotLoadTheSlaPolicy: 'Could not load the SLA policy.',
    loading: 'Loading…',
    backToSlaPolicies: '← Back to SLA policies',
    policyNotFound: 'Policy not found.',
    active: 'Active',
    disabled: 'Disabled',
    idIdTenantTenantId: 'ID {{id}} · Tenant {{tenant_id}}',
    n1stReply: '1st reply',
    resolution: 'Resolution',
    actionOnBreach: 'Action on breach',
    priority: 'Priority',
    platformScope: 'Platform (scope)',
    allPlatforms: 'All platforms',
    tagScope: 'Tag (scope)',
    createdOn: 'Created on',
    updatedOn: 'Updated on',
    escalate: 'Escalate',
  },
  targetingEngagement: {
    external: 'external',
    actionComment: 'comment on',
    actionReact: 'react to',
    actionShare: 'share',
    publishOwnedPage: 'Publish (owned page)',
    share: 'Share',
    comment: 'Comment',
    react: 'React',
    text: 'Text',
    video: 'Video',
    displays: 'Displays',
    shareShareUrlPreparedToOpen: 'Share = share URL prepared to open manually (the API does not share silently).',
    commentOnOwnedMediaOnlyReaction: 'Comment on owned media only; reaction not available via the API.',
    commentInsertionSubjectToTheYoutube: 'Comment insertion subject to the YouTube API quota.',
    notSupportedAwaitingAppReview: 'Not supported (awaiting app review).',
    discover: 'Discover',
    select: 'Select',
    content: 'Content',
    act: 'Act',
    targetedExternalEngagement: 'Targeted external engagement',
    discoverPagesGroupsInfluencersBuildA: 'Discover pages/groups/influencers, build a basket, choose or let the AI suggest the content, then act — manually by default. Official APIs only, never wild collection.',
    targetedEngagementIsNotAvailableOn: '⚠ Targeted engagement is not available on this environment yet.',
    previous: '← Previous',
    next: 'Next →',
    discoveryFailed: 'Discovery failed.',
    targetResolvedName: 'Target resolved: {{name}}',
    couldNotResolveThisTarget: 'Could not resolve this target.',
    ownedPages: 'Owned pages',
    publicPagesInfluencers: 'Public pages / influencers',
    platform: 'Platform',
    allPlatforms: 'All platforms',
    searchAPageAnInfluencer: 'Search a page / an influencer…',
    search: 'Search',
    nameATargetByHandleOr: 'Name a target by handle or URL (official search, no collection)',
    eGKoinoniaOrHttpsFacebook: 'e.g. @koinonia or https://facebook.com/…',
    resolve: 'Resolve',
    loading: 'Loading…',
    noOwnedPageConnectedForThis: 'No owned page connected for this platform.',
    runASearchOrResolveA: 'Run a search or resolve a target by URL.',
    remove: 'Remove',
    add: '+ Add',
    yourBasketIsEmptyGoBack: 'Your basket is empty — go back to the “Discover” step.',
    lengthTargetSSelected: '{{length}} target(s) selected.',
    owned: 'owned',
    campaignName: 'Campaign name',
    eGCommunityEngagementOctober: 'e.g. Community engagement — October',
    actionType: 'Action type',
    iWrite: 'I write',
    aiSuggestion: 'AI suggestion',
    format: 'Format',
    yourContentComment: 'Your content / comment',
    writeAnAuthenticMessageNoUnsolicited: 'Write an authentic message (no unsolicited promotion)…',
    media: 'Media',
    removeTheMedia: 'Remove the media',
    chooseAMedia: '+ Choose a media',
    theAiWillSelectTheRelevant: 'The AI will select the relevant posts of each target and draft comments/content ({{v}}) at the next step. Nothing is sent automatically in manual mode.',
    chooseAMedia2: 'Choose a media',
    searchTheMediaLibrary: 'Search the media library…',
    noMedia: 'No media.',
    lengthSuggestionSGenerated: '{{length}} suggestion(s) generated.',
    suggestionsStoppedInsufficientCredits: '{{done}} suggestion(s) generated; {{stopped}} stopped: insufficient AI credits — top up to continue.',
    generatingTheSuggestionsFailed: 'Generating the suggestions failed.',
    selectAtLeastOneExecutableSuggestion: 'Select at least one executable suggestion.',
    campaignSuspendedCircuitBreakerAfterCons: 'Campaign suspended (circuit breaker) after consecutive errors.',
    executedTotalActionSExecuted: '{{executed}}/{{total}} action(s) executed.',
    executionFailed: 'Execution failed.',
    autonomousMode: 'Autonomous mode',
    recommendationOnlyVForbidSAutomation: 'Recommendation only: {{v}} forbid(s) automation (see the reasons below). Karexia prepares, you publish.',
    runsTheApprovedActionsAutomaticallyWithi: 'Runs the approved actions automatically, within the per-target/platform rate limits and after the compliance filter.',
    disabledAutonomousModeMustFirstBe: 'Disabled. Autonomous mode must first be enabled in the automation policy.',
    openTheAutomationPolicy: 'Open the automation policy →',
    autonomyPerPlatform: 'Autonomy per platform',
    honestPerPlatformLimitsTos: 'Honest per-platform limits (ToS)',
    targetedPosts: 'Targeted posts',
    pasteTheUrlOfThePost: 'Paste the URL of the post to',
    forEachTargetReadingThirdParty: 'for each target (reading third-party posts = manual paste, never collection).',
    postUrlName: 'Post URL — {{name}}',
    optionalBriefForTheAiTone: 'Optional brief for the AI (tone, angle)…',
    aiBrief: 'AI brief',
    generateAiSuggestions: '✨ Generate AI suggestions',
    launchAutonomous: 'Launch (autonomous)',
    runSizeActionS: 'Run {{size}} action(s)',
    generateAiSuggestionsTheAiPicks: 'Generate AI suggestions: the AI picks the relevant posts of each target and drafts. You approve each action before it runs (manual mode).',
    selectTheSuggestion: 'Select the suggestion',
    relevanceV: 'relevance {{v}}%',
    whyRelevanceReason: 'Why: {{relevance_reason}}',
    blocked: 'Blocked:',
    actionNotAvailableOnThisTarget: 'action not available on this target',
    executed: '✓ Executed',
    viaPost: ' (via post)',
    shareLinkPrepared: 'Share link prepared —',
    openToShare: 'open to share',
    blocked2: 'Blocked',
    failed: 'Failed',
    viaPublishingService: ' (post created via the publishing service)',
    viaPublishingServiceJobs: ' (post created via the publishing service — job {{jobs}})',
    autonomousModeEnabledForThisCampaign: 'Autonomous mode enabled for this campaign.',
    manualModeRestored: 'Manual mode restored.',
    couldNotChangeMode: 'Could not change the mode.',
    campaignSavedAutonomous: 'Campaign saved (autonomous).',
    campaignSavedManual: 'Campaign saved (manual).',
    savingFailed: 'Saving failed.',
    campaignModeAutonomous: 'Campaign: autonomous.',
    campaignModeManual: 'Campaign: manual.',
    saveTheCampaign: '💾 Save the campaign',
    couldNotLoadTheCampaigns: 'Could not load the campaigns.',
    autonomousModeEnabled: 'Autonomous mode enabled.',
    campaignDeleted: 'Campaign deleted.',
    deletionFailed: 'Deletion failed.',
    savedCampaigns: 'Saved campaigns',
    targetCountStatus: '{{count}} target(s) · {{status}}',
    autonomous: 'Autonomous',
    manual: 'Manual',
    autonomousModeName: 'Autonomous mode — {{name}}',
    deleteTheCampaignName: 'Delete the campaign {{name}}',
    delete: 'Delete',
    deleteTheCampaign: 'Delete the campaign',
    deleteCampaignConfirm: 'Delete “{{name}}”? Actions already executed on the networks are not undone.',
  },
  themesPage: {
    recompute: '↻ Recompute',
    question: 'Question',
    praise: 'Praise',
    complaint: 'Complaint',
    request: 'Request',
    discussion: 'Discussion',
    other: 'Other',
    couldNotLoadTheThemes: 'Could not load the themes.',
    themesRecomputedNItemSProcessed: 'Themes recomputed ({{n}} item(s) processed).',
    themesPendingInsufficientCredits: 'Insufficient AI credits: {{n}} theme(s) awaiting AI labelling — they will be processed on the next recompute after a top-up.',
    themeRecomputationComplete: 'Theme recomputation complete.',
    theRecomputationFailed: 'The recomputation failed.',
    themeAddedToTheContentEngine: 'Theme added to the content engine: “{{theme}}”',
    addingTheThemeFailed: 'Adding the theme failed.',
    theRejectionFailed: 'The rejection failed.',
    themesFromComments: 'Themes from comments',
    topicsDrawnFromDeIdentifiedComments: 'Topics drawn from (de-identified) comments, recurring audience questions and recommended themes to publish. “Use as a theme” feeds the content engine.',
    commentProfilingAnalysisThemesIsDisabled: 'Comment profiling (analysis → themes) is disabled. Enable it in',
    theAutomationPolicy: 'the automation policy',
    law25ConsentToGenerateThemes: '(Law 25 consent) to generate themes.',
    themeThemeSent: 'Theme “{{theme}}” sent.',
    openInTheContentEngine: 'Open in the content engine →',
    themeAnalysisIsNotAvailableOn: '⚠ Theme analysis is not available on this environment yet.',
    themeRecommendations: 'Theme recommendations',
    useAsATheme: 'Use as a theme',
    reject: 'Reject',
    detectedTopics: 'Detected topics',
    loading: 'Loading…',
    noTopicDetectedYetItTakes: 'No topic detected yet (it takes at least 5 similar comments per group).',
    commentS: 'comment(s)',
    representativeQuestions: 'Representative questions',
    audienceQuestions: 'Audience questions',
  },
};

export default en;
